import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { PageHeader } from '@/components/PageHeader';
import { StatusDot } from '@/components/StatusDot';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Pause, Play, Send, Loader2, Activity, FileText, Sliders, Trash2, ChevronLeft, ShieldCheck, Flame, Gauge, Sparkles, Megaphone, Rocket, ThumbsUp, MessageCircle, BookOpen, Brain, Library, Zap, Terminal, TrendingUp, Heart, ShoppingBag, Smartphone, Briefcase, GitBranch, Calendar, BarChart3, Clock, X, Check } from 'lucide-react';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuLabel, DropdownMenuSeparator } from '@/components/ui/dropdown-menu';
import { HealthBadge } from '@/components/HealthBadge';
import { AnalyticsPanel } from '@/components/AnalyticsPanel';
import { SchedulePanel } from '@/components/SchedulePanel';
import { TakeoverButton } from '@/components/TakeoverDialog';
import { AgentMemory } from '@/components/AgentMemory';
import { toast } from 'sonner';
import { relativeTime, shortId, formatDate } from '@/lib/utils';
import { BROWSER_PROFILES, browserProfileLabel } from '@/lib/farm';

export default function AgentDetail() {
  const { agentId } = useParams();
  const nav = useNavigate();
  const qc = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ['agent', agentId],
    queryFn: () => api.get(`/agents/${agentId}`).then(r => r.data),
    refetchInterval: 10000,
  });

  const pauseMut = useMutation({ mutationFn: () => api.post(`/agents/${agentId}/pause`), onSuccess: () => { toast.success('Agent paused'); qc.invalidateQueries({ queryKey: ['agent', agentId] }); } });
  const resumeMut = useMutation({ mutationFn: () => api.post(`/agents/${agentId}/resume`), onSuccess: () => { toast.success('Agent resumed'); qc.invalidateQueries({ queryKey: ['agent', agentId] }); } });
  const deleteMut = useMutation({ mutationFn: () => api.delete(`/agents/${agentId}`), onSuccess: () => { toast.success('Agent archived'); nav('/agents'); } });
  const loginCheckMut = useMutation({
    mutationFn: () => api.post(`/agents/${agentId}/tasks`, { kind: 'login_check', instruction: 'Credential login check.' }).then(r => r.data),
    onSuccess: (t) => { toast.success('Login check started', { description: 'Watch the Timeline tab for the reddit.login_status event.' }); nav(`/tasks/${t.id}`); },
    onError: (e) => toast.error(e?.response?.data?.detail || 'Failed to start'),
  });
  const scoreCheckMut = useMutation({
    mutationFn: () => api.post(`/agents/${agentId}/tasks`, { kind: 'score_check', instruction: 'Measure current reCAPTCHA v3 score.' }).then(r => r.data),
    onSuccess: (t) => { toast.success('Score check started', { description: 'Takes ~30s. Opens task page so you can watch live.' }); nav(`/tasks/${t.id}`); },
    onError: (e) => toast.error(e?.response?.data?.detail || 'Failed to start'),
  });
  const mobileFidelityMut = useMutation({
    mutationFn: () => api.post(`/agents/${agentId}/tasks`, { kind: 'mobile_fidelity_audit', instruction: JSON.stringify({ mobile_profile: 'pixel_7', fresh_profile: true }) }).then(r => r.data),
    onSuccess: (t) => { toast.success('Mobile fidelity audit started', { description: 'Checks Pixel 7 spoof consistency before signup.' }); nav(`/tasks/${t.id}`); },
    onError: (e) => toast.error(e?.response?.data?.detail || 'Failed to start'),
  });
  const warmupMut = useMutation({
    mutationFn: ({ minutes = 8, stage = 'platform' } = {}) => api.post(`/agents/${agentId}/tasks`, { kind: 'warmup', instruction: `${minutes} stage=${stage}` }).then(r => r.data),
    onSuccess: (t) => { toast.success('Warmup started', { description: 'Runs in background.' }); nav(`/tasks/${t.id}`); },
    onError: (e) => toast.error(e?.response?.data?.detail || 'Failed to start'),
  });
  const smartLoginMut = useMutation({
    mutationFn: () => api.post(`/agents/${agentId}/tasks`, { kind: 'smart_login', instruction: 'Score-gated login. Warms up if score is low.' }).then(r => r.data),
    onSuccess: (t) => { toast.success('Smart login started', { description: 'Will check score, warm up if needed, then attempt login.' }); nav(`/tasks/${t.id}`); },
    onError: (e) => toast.error(e?.response?.data?.detail || 'Failed to start'),
  });
  const warmupCampaignMut = useMutation({
    mutationFn: (body) => api.post(`/agents/${agentId}/warmup-campaign`, body).then(r => r.data),
    onSuccess: (d) => {
      toast.success('Warmup campaign scheduled', {
        description: `${d.scheduled} warmup tasks across ${d.duration_hours}h. Smart-login gated until ${new Date(d.warmup_until_at).toLocaleString()}.`,
      });
      qc.invalidateQueries({ queryKey: ['agent', agentId] });
    },
    onError: (e) => toast.error(e?.response?.data?.detail || 'Failed to schedule'),
  });
  const cancelWarmupMut = useMutation({
    mutationFn: () => api.post(`/agents/${agentId}/warmup-campaign/cancel`).then(r => r.data),
    onSuccess: () => { toast.success('Warmup window cleared'); qc.invalidateQueries({ queryKey: ['agent', agentId] }); },
    onError: (e) => toast.error(e?.response?.data?.detail || 'Failed'),
  });
  const clearCookiesMut = useMutation({
    mutationFn: () => api.delete(`/agents/${agentId}/cookies`).then(r => r.data),
    onSuccess: () => { toast.success('Cleared seed cookies + profile'); qc.invalidateQueries({ queryKey: ['agent', agentId] }); },
    onError: () => toast.error('Failed to clear'),
  });
  const promoteMut = useMutation({
    mutationFn: ({ llm_model } = {}) => api.post(`/agents/${agentId}/tasks`, { kind: 'promote', instruction: '', llm_model }).then(r => r.data),
    onSuccess: (t) => { toast.success('Promote task started', { description: 'Agent will discover subs & post a contextual comment.' }); nav(`/tasks/${t.id}`); },
    onError: (e) => toast.error(e?.response?.data?.detail || 'Failed to start'),
  });
  const karmaFarmMut = useMutation({
    mutationFn: ({ llm_model } = {}) => api.post(`/agents/${agentId}/tasks`, { kind: 'karma_farm', instruction: '', llm_model }).then(r => r.data),
    onSuccess: (t) => { toast.success('Karma-farm started'); nav(`/tasks/${t.id}`); },
    onError: (e) => toast.error(e?.response?.data?.detail || 'Failed to start'),
  });
  const boostMut = useMutation({
    mutationFn: ({ llm_model } = {}) => api.post(`/agents/${agentId}/tasks`, { kind: 'boost_comment', instruction: '', llm_model }).then(r => r.data),
    onSuccess: (t) => { toast.success('Boost started', { description: 'Picked up next pending boost from org queue.' }); nav(`/tasks/${t.id}`); },
    onError: (e) => toast.error(e?.response?.data?.detail || 'Failed to start'),
  });
  const replyMentionMut = useMutation({
    mutationFn: ({ llm_model } = {}) => api.post(`/agents/${agentId}/tasks`, { kind: 'reply_mention', instruction: '', llm_model }).then(r => r.data),
    onSuccess: (t) => { toast.success('Reply-to-mentions started'); nav(`/tasks/${t.id}`); },
    onError: (e) => toast.error(e?.response?.data?.detail || 'Failed to start'),
  });


  if (isLoading) return <div className="text-sm text-[hsl(var(--text-3))]" data-testid="loading-skeleton">Loading agent…</div>;
  if (!data?.agent) return <div className="text-sm text-[hsl(var(--text-3))]">Agent not found.</div>;
  const a = data.agent;

  return (
    <div className="space-y-6" data-testid="agent-detail-page">
      <div>
        <button onClick={() => nav('/agents')} className="inline-flex items-center gap-1 text-xs text-[hsl(var(--text-3))] hover:text-[hsl(var(--text-1))] transition-colors" data-testid="back-to-agents-button">
          <ChevronLeft className="h-3.5 w-3.5" /> All agents
        </button>
      </div>
      <PageHeader
        eyebrow={`Agent · ${a.platform}`}
        title={a.name}
        description={
          <div className="flex items-center gap-4 mt-1 flex-wrap">
            <div className="flex items-center gap-2"><StatusDot state={a.status} /><span className="font-mono text-xs text-[hsl(var(--text-3))]">{a.id}</span></div>
            {data?.health && <HealthBadge health={data.health} size="md" />}
          </div>
        }
        actions={
          <div className="flex flex-wrap gap-2">
            <Button variant="secondary" size="sm" onClick={() => scoreCheckMut.mutate()} disabled={scoreCheckMut.isPending} data-testid="agent-score-check-button">
              {scoreCheckMut.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Gauge className="mr-2 h-4 w-4" />}
              Check score
            </Button>
            <Button variant="secondary" size="sm" onClick={() => mobileFidelityMut.mutate()} disabled={mobileFidelityMut.isPending} data-testid="agent-mobile-fidelity-button">
              {mobileFidelityMut.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Smartphone className="mr-2 h-4 w-4" />}
              Mobile audit
            </Button>
            <WarmupSmartButton
              agent={a}
              onQuickWarmup={(minutes) => warmupMut.mutate({ minutes, stage: 'platform' })}
              onCampaign={(preset) => warmupCampaignMut.mutate({ preset, include_generic_stage: true })}
              onCancelCampaign={() => cancelWarmupMut.mutate()}
              quickPending={warmupMut.isPending}
              campaignPending={warmupCampaignMut.isPending}
              cancelPending={cancelWarmupMut.isPending}
            />
            <TakeoverButton agentId={a.id} runtime={a.runtime} vmosPadCode={a.vmos_pad_code} />
            {a.credential_id && (
              <>
                <Button variant="secondary" size="sm" onClick={() => loginCheckMut.mutate()} disabled={loginCheckMut.isPending} data-testid="agent-login-check-button">
                  {loginCheckMut.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <ShieldCheck className="mr-2 h-4 w-4" />}
                  Login check
                </Button>
                <Button variant="secondary" size="sm" onClick={() => smartLoginMut.mutate()} disabled={smartLoginMut.isPending} data-testid="agent-smart-login-button">
                  {smartLoginMut.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Sparkles className="mr-2 h-4 w-4" />}
                  Smart login
                </Button>
              </>
            )}
            {a.status === 'paused' ? (
              <Button size="sm" variant="secondary" onClick={() => resumeMut.mutate()} disabled={resumeMut.isPending} data-testid="agent-resume-button"><Play className="mr-2 h-4 w-4" /> Resume</Button>
            ) : (
              <Button size="sm" variant="secondary" onClick={() => pauseMut.mutate()} disabled={pauseMut.isPending} data-testid="agent-pause-button"><Pause className="mr-2 h-4 w-4" /> Pause</Button>
            )}
            <SubmitTaskDialog agentId={a.id} onSubmitted={() => qc.invalidateQueries({ queryKey: ['agent', agentId] })} />
          </div>
        }
      />

      <Tabs defaultValue="overview" data-testid="agent-detail-tabs">
        <TabsList>
          <TabsTrigger value="overview" data-testid="tab-overview">Overview</TabsTrigger>
          <TabsTrigger value="timeline" data-testid="tab-timeline">Timeline</TabsTrigger>
          <TabsTrigger value="tasks" data-testid="tab-tasks">Tasks</TabsTrigger>
          <TabsTrigger value="stealth" data-testid="tab-stealth">Stealth</TabsTrigger>
          <TabsTrigger value="promote" data-testid="tab-promote">Promote</TabsTrigger>
          <TabsTrigger value="schedule" data-testid="tab-schedule">Schedule</TabsTrigger>
          <TabsTrigger value="analytics" data-testid="tab-analytics">Analytics</TabsTrigger>
          <TabsTrigger value="insights" data-testid="tab-insights">Insights</TabsTrigger>
          <TabsTrigger value="memory" data-testid="tab-memory">Memory</TabsTrigger>
          <TabsTrigger value="settings" data-testid="tab-settings">Settings</TabsTrigger>
          <TabsTrigger value="danger" data-testid="tab-danger">Danger zone</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="space-y-4">
          <div className="grid gap-4 md:grid-cols-4">
            <Metric label="Total actions" value={a.total_actions} />
            <Metric label="Real / Fake" value={`${a.real_actions} / ${a.fake_actions}`} />
            <Metric label="Risk score" value={(a.risk_score || 0).toFixed(2)} />
            <RecaptchaScoreMetric events={data.recent_events} />
          </div>
          <Card title="Mission">
            <p className="whitespace-pre-wrap text-sm text-[hsl(var(--text-2))]">{a.mission}</p>
          </Card>
          <div className="grid gap-4 md:grid-cols-2">
            <Card title="Configuration">
              <dl className="grid grid-cols-[120px_1fr] gap-y-2 text-sm">
                <Row k="Model" v={<span className="font-mono text-xs">{a.llm_model}</span>} />
                <Row k="Stealth" v={a.stealth_level} />
                <Row k="Timezone" v={a.timezone} />
                <Row k="Active hours" v={`${a.active_hours_start}–${a.active_hours_end}`} />
                <Row k="Credential" v={a.credential_id ? <span className="font-mono text-xs">{shortId(a.credential_id)}</span> : '—'} />
                <Row k="Proxy" v={a.proxy_id ? <span className="font-mono text-xs">{shortId(a.proxy_id)}</span> : '—'} />
                <Row k="Workflow" v={a.workflow_id ? <span className="font-mono text-xs">{a.workflow_id}</span> : 'Not scheduled'} />
              </dl>
            </Card>
            <Card title="Lifecycle">
              <dl className="grid grid-cols-[120px_1fr] gap-y-2 text-sm">
                <Row k="Created" v={formatDate(a.created_at)} />
                <Row k="Updated" v={formatDate(a.updated_at)} />
                <Row k="Last run" v={a.last_run_at ? relativeTime(a.last_run_at) : 'Never'} />
                <Row k="Last error" v={a.last_error ? <span className="text-[hsl(var(--danger))]">{a.last_error}</span> : '—'} />
              </dl>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="timeline" className="space-y-2">
          <Card title="Event timeline">
            {data.recent_events?.length ? (
              <ol className="relative pl-6">
                <span className="absolute left-2 top-0 bottom-0 w-px bg-white/10" />
                {data.recent_events.map((e) => (
                  <li key={e.id} className="relative py-3" data-testid={`timeline-item-${e.id}`}>
                    <span className="absolute -left-[19px] top-5 h-2 w-2 rounded-full bg-[hsl(var(--primary))]" />
                    <div className="flex items-center gap-2 text-sm">
                      <span className="rounded border border-white/10 bg-[hsl(var(--surface-2))] px-1.5 py-0.5 font-mono text-[10px] uppercase tracking-wider">{e.kind}</span>
                      <span className="text-[hsl(var(--text-3))] font-mono text-xs">{relativeTime(e.created_at)}</span>
                    </div>
                    {e.payload && Object.keys(e.payload).length > 0 && (
                      <pre className="mt-1.5 rounded-md border border-white/5 bg-black/30 p-2 text-xs font-mono overflow-auto">{JSON.stringify(e.payload, null, 2)}</pre>
                    )}
                  </li>
                ))}
              </ol>
            ) : <div className="text-sm text-[hsl(var(--text-3))]">No events yet.</div>}
          </Card>
        </TabsContent>

        <TabsContent value="tasks" className="space-y-2">
          <Card title="Recent tasks">
            {data.recent_tasks?.length ? (
              <div className="divide-y divide-white/5">
                {data.recent_tasks.map((t) => (
                  <button key={t.id} onClick={() => nav(`/tasks/${t.id}`)} className="flex w-full items-center gap-4 py-3 text-left hover:bg-[hsl(var(--surface-2))] transition-colors rounded-md px-2" data-testid={`agent-task-${t.id}`}>
                    <span className="rounded border border-white/10 bg-[hsl(var(--surface-2))] px-1.5 py-0.5 font-mono text-[10px] uppercase tracking-wider">{t.kind}</span>
                    <StatusDot state={t.status} />
                    <div className="min-w-0 flex-1 truncate text-sm">
                      {t.error
                        ? <span className="text-[hsl(var(--danger))]">{t.error}</span>
                        : t.target_url
                          ? t.target_url
                          : <span className="text-[hsl(var(--text-3))]">{t.kind === 'upvote' || t.kind === 'comment' ? 'needs target URL' : '—'}</span>}
                    </div>
                    <span className="text-xs text-[hsl(var(--text-3))]">{relativeTime(t.created_at)}</span>
                  </button>
                ))}
              </div>
            ) : <div className="text-sm text-[hsl(var(--text-3))]">No tasks yet.</div>}
          </Card>
        </TabsContent>

        <TabsContent value="stealth" className="space-y-4">
          <StealthPanel
            agent={a}
            events={data.recent_events}
            agentId={agentId}
            onWarmupCampaign={(b) => warmupCampaignMut.mutate(b)}
            warmupPending={warmupCampaignMut.isPending}
            onClearCookies={() => clearCookiesMut.mutate()}
            clearingCookies={clearCookiesMut.isPending}
          />
        </TabsContent>

        <TabsContent value="promote" className="space-y-4">
          <PromotePanel
            agentId={agentId}
            agent={a}
            onPromote={(model) => promoteMut.mutate({ llm_model: model })}
            onKarmaFarm={(model) => karmaFarmMut.mutate({ llm_model: model })}
            onBoost={(model) => boostMut.mutate({ llm_model: model })}
            onReplyMention={(model) => replyMentionMut.mutate({ llm_model: model })}
            promotePending={promoteMut.isPending}
            karmaFarmPending={karmaFarmMut.isPending}
            boostPending={boostMut.isPending}
            replyMentionPending={replyMentionMut.isPending}
          />
        </TabsContent>

        <TabsContent value="insights" className="space-y-4">
          <InsightsPanel agentId={agentId} />
        </TabsContent>

        <TabsContent value="schedule" className="space-y-4">
          <SchedulePanel agentId={agentId} />
        </TabsContent>

        <TabsContent value="analytics" className="space-y-4">
          <AnalyticsPanel agentId={agentId} />
        </TabsContent>

        <TabsContent value="settings" className="space-y-2">
          <AgentSettings agent={a} proxy={data?.proxy} credential={data?.credential} lastLoggedIn={data?.last_logged_in} />
        </TabsContent>

        <TabsContent value="memory" className="space-y-2">
          <AgentMemory agentId={agentId} />
        </TabsContent>

        <TabsContent value="danger" className="space-y-2">
          <Card title="Danger zone">
            <p className="text-sm text-[hsl(var(--text-3))]">Archiving an agent stops it from running further tasks. This action can be undone by unarchiving from the admin console.</p>
            <Button variant="destructive" className="mt-4" onClick={() => deleteMut.mutate()} disabled={deleteMut.isPending} data-testid="agent-archive-button">
              <Trash2 className="mr-2 h-4 w-4" /> Archive agent
            </Button>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}

function Metric({ label, value }) {
  return (
    <div className="rounded-lg border border-white/5 bg-[hsl(var(--surface-1))] p-4">
      <div className="text-xs uppercase tracking-wider text-[hsl(var(--text-3))]">{label}</div>
      <div className="mt-2 text-2xl font-semibold tabular">{value}</div>
    </div>
  );
}


/** Smart warmup button: one-click 8-minute warmup by default, dropdown for
    duration choices + campaign launch, disabled while a campaign is active. */
function WarmupSmartButton({ agent, onQuickWarmup, onCampaign, onCancelCampaign, quickPending, campaignPending, cancelPending }) {
  const cfg = agent.config || {};
  const warmupUntil = cfg.warmup_until_at ? new Date(cfg.warmup_until_at) : null;
  const active = warmupUntil && warmupUntil > new Date();
  const hoursLeft = active ? Math.max(0, Math.round((warmupUntil - new Date()) / 3600000)) : 0;
  const anyPending = quickPending || campaignPending || cancelPending;

  if (active) {
    return (
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="secondary" size="sm" disabled={anyPending} data-testid="warmup-smart-button-active">
            <Clock className="mr-2 h-4 w-4 text-[hsl(var(--warning))]" />
            Warmup active · {hoursLeft}h left
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-56">
          <DropdownMenuLabel className="text-xs">Campaign running</DropdownMenuLabel>
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={onCancelCampaign} className="text-[hsl(var(--danger))]" data-testid="warmup-cancel">
            <X className="mr-2 h-4 w-4" /> Cancel window
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    );
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="secondary" size="sm" disabled={anyPending} data-testid="warmup-smart-button">
          {anyPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Flame className="mr-2 h-4 w-4" />}
          Warmup
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-64">
        <DropdownMenuLabel className="text-xs">One-shot (runs now)</DropdownMenuLabel>
        <DropdownMenuItem onClick={() => onQuickWarmup(30)} data-testid="warmup-quick-30m">30 min session</DropdownMenuItem>
        <DropdownMenuItem onClick={() => onQuickWarmup(60)} data-testid="warmup-quick-60m">60 min session</DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuLabel className="text-xs">Aged campaign (2-stage, Poisson-scheduled)</DropdownMenuLabel>
        <DropdownMenuItem onClick={() => onCampaign('half_day_4h')} data-testid="warmup-campaign-4h">4h campaign · 4 sessions</DropdownMenuItem>
        <DropdownMenuItem onClick={() => onCampaign('day_24h')} data-testid="warmup-campaign-24h">24h campaign · 6 sessions</DropdownMenuItem>
        <DropdownMenuItem onClick={() => onCampaign('aged_48h')} data-testid="warmup-campaign-48h">48h campaign · 10 sessions</DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}


/** Preset grid used inside the Stealth tab. Clickable cards for every duration. */
function WarmupPresetGrid({ onPick, disabled, pending }) {
  const PRESETS = [
    { key: 'quick_30m',   label: '30 min',   hint: '1 session · one-shot' },
    { key: 'hour_60m',    label: '60 min',   hint: '2 sessions' },
    { key: 'half_day_4h', label: '4 hours',  hint: '4 sessions' },
    { key: 'day_24h',     label: '24 hours', hint: '6 sessions · full day' },
    { key: 'aged_48h',    label: '48 hours', hint: '10 sessions · aged profile' },
  ];
  return (
    <div className="grid gap-2 grid-cols-2 sm:grid-cols-5" data-testid="warmup-preset-grid">
      {PRESETS.map(p => (
        <button
          key={p.key}
          onClick={() => onPick(p.key)}
          disabled={disabled}
          data-testid={`warmup-preset-${p.key}`}
          className="group rounded-md border border-white/5 bg-[hsl(var(--surface-2))] px-3 py-2.5 text-left hover:border-[hsl(var(--primary))]/40 hover:bg-[hsl(var(--surface-3))] transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
        >
          <div className="flex items-center gap-1.5 text-xs font-semibold">
            {pending && <Loader2 className="h-3 w-3 animate-spin" />}
            {!pending && <Flame className="h-3 w-3 text-[hsl(var(--primary))]" />}
            {p.label}
          </div>
          <div className="text-[10px] text-[hsl(var(--text-3))] mt-0.5">{p.hint}</div>
        </button>
      ))}
    </div>
  );
}

function RecaptchaScoreMetric({ events }) {
  const latest = React.useMemo(() => {
    const scoreEvents = (events || []).filter(e => e.kind.startsWith('recaptcha.score'));
    if (!scoreEvents.length) return null;
    const e = scoreEvents[0];
    return { score: e.payload?.score, ts: e.created_at };
  }, [events]);
  const score = latest?.score;
  const hue = score == null ? 'text-[hsl(var(--text-3))]'
    : score >= 0.7 ? 'text-[hsl(var(--success))]'
    : score >= 0.4 ? 'text-[hsl(var(--warning))]'
    : 'text-[hsl(var(--danger))]';
  return (
    <div className="rounded-lg border border-white/5 bg-[hsl(var(--surface-1))] p-4" data-testid="metric-recaptcha-score">
      <div className="text-xs uppercase tracking-wider text-[hsl(var(--text-3))]">reCAPTCHA v3 score</div>
      <div className={`mt-2 text-2xl font-semibold tabular ${hue}`}>{score != null ? score.toFixed(2) : '—'}</div>
      <div className="mt-1 text-[10px] uppercase tracking-wider text-[hsl(var(--text-3))]">
        {score == null ? 'Run "Check score" to measure' : score >= 0.7 ? 'ready for login' : score >= 0.4 ? 'needs warmup' : 'blocked — warm up deeply'}
      </div>
    </div>
  );
}

function Card({ title, children }) {
  return (
    <div className="rounded-lg border border-white/5 bg-[hsl(var(--surface-1))]">
      <div className="border-b border-white/5 px-5 py-3 text-sm font-medium">{title}</div>
      <div className="p-5">{children}</div>
    </div>
  );
}
function Row({ k, v }) {
  return (
    <>
      <dt className="text-[hsl(var(--text-3))]">{k}</dt>
      <dd>{v}</dd>
    </>
  );
}

function SubmitTaskDialog({ agentId, onSubmitted }) {
  const [open, setOpen] = React.useState(false);
  const [form, setForm] = React.useState({ instruction: '', target_url: '', kind: 'natural', llm_model: '' });
  const isIntelligent = ['promote','karma_farm','boost_comment','reply_mention'].includes(form.kind);
  const mut = useMutation({
    mutationFn: (payload) => {
      const body = { ...payload };
      if (!body.llm_model) delete body.llm_model;
      return api.post(`/agents/${agentId}/tasks`, body).then(r => r.data);
    },
    onSuccess: () => { toast.success('Task enqueued'); setOpen(false); onSubmitted?.(); setForm({ instruction: '', target_url: '', kind: 'natural', llm_model: '' }); },
    onError: (e) => toast.error(e?.response?.data?.detail || 'Submit failed'),
  });
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm" data-testid="agent-submit-task-button"><Send className="mr-2 h-4 w-4" /> Submit task</Button>
      </DialogTrigger>
      <DialogContent data-testid="submit-task-dialog">
        <DialogHeader>
          <DialogTitle>Submit task</DialogTitle>
          <DialogDescription>Pick a kind; Temporal will queue and the worker picks it up.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label>Kind</Label>
            <Select value={form.kind} onValueChange={(v) => setForm({ ...form, kind: v })}>
              <SelectTrigger data-testid="task-kind-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="natural">Natural (lurk + browse)</SelectItem>
                <SelectItem value="warmup">Warmup (Google referrer + power-user lurk)</SelectItem>
                <SelectItem value="score_check">Score check (reCAPTCHA v3)</SelectItem>
                <SelectItem value="login_check">Login check</SelectItem>
                <SelectItem value="smart_login">Smart login (score-gated)</SelectItem>
                <SelectItem value="upvote">Upvote target URL</SelectItem>
                <SelectItem value="comment">Comment on target URL</SelectItem>
                <SelectItem value="promote">Promote (LLM · contextual post)</SelectItem>
                <SelectItem value="karma_farm">Karma farm (LLM · organic engagement)</SelectItem>
                <SelectItem value="boost_comment">Boost comment (upvote + reply)</SelectItem>
                <SelectItem value="reply_mention">Reply to mentions (endorse)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {isIntelligent && (
            <div className="space-y-1.5">
              <Label>LLM model (override)</Label>
              <Select value={form.llm_model || 'default'} onValueChange={(v) => setForm({ ...form, llm_model: v === 'default' ? '' : v })}>
                <SelectTrigger data-testid="task-llm-model-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="default">Use default ({form.kind === 'promote' ? 'Claude Sonnet 4.5' : 'Gemini 2.5 Flash'})</SelectItem>
                  <SelectItem value="anthropic/claude-sonnet-4.5">Claude Sonnet 4.5 (best for writing)</SelectItem>
                  <SelectItem value="google/gemini-2.5-flash">Gemini 2.5 Flash (fast & cheap)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}
          <div className="space-y-1.5">
            <Label>Instruction</Label>
            <Textarea rows={5} value={form.instruction} onChange={(e) => setForm({ ...form, instruction: e.target.value })} placeholder='e.g. "8" for warmup minutes, "strategy=recent" for promote, a comment body, or a natural-language brief.' data-testid="task-instruction-input" />
          </div>
          <div className="space-y-1.5">
            <Label>Target URL (upvote / comment / boost_comment)</Label>
            <Input value={form.target_url} onChange={(e) => setForm({ ...form, target_url: e.target.value })} placeholder="https://www.reddit.com/r/…" data-testid="task-url-input" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="secondary" onClick={() => setOpen(false)}>Cancel</Button>
          <Button onClick={() => mut.mutate(form)} disabled={mut.isPending} data-testid="task-submit-button">
            {mut.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Enqueue task
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function AgentSettings({ agent, proxy, credential, lastLoggedIn }) {
  const qc = useQueryClient();
  const nav = useNavigate();
  const [form, setForm] = React.useState({
    name: agent.name, mission: agent.mission,
    timezone: agent.timezone,
    active_hours_start: agent.active_hours_start,
    active_hours_end: agent.active_hours_end,
  });
  const mut = useMutation({
    mutationFn: (p) => api.patch(`/agents/${agent.id}`, p).then(r => r.data),
    onSuccess: () => { toast.success('Settings saved'); qc.invalidateQueries({ queryKey: ['agent', agent.id] }); },
    onError: () => toast.error('Save failed'),
  });
  const bindMut = useMutation({
    mutationFn: (p) => api.patch(`/agents/${agent.id}`, p).then(r => r.data),
    onSuccess: () => { toast.success('Binding updated'); qc.invalidateQueries({ queryKey: ['agent', agent.id] }); },
    onError: (e) => toast.error(e?.response?.data?.detail || 'Bind failed'),
  });
  const loginCheckMut = useMutation({
    mutationFn: () => api.post(`/agents/${agent.id}/tasks`, { kind: 'login_check' }).then(r => r.data),
    onSuccess: (t) => { toast.success('Login check started'); nav(`/tasks/${t.id}`); },
  });
  const proxyCheckMut = useMutation({
    mutationFn: () => api.post(`/proxies/${agent.proxy_id}/health-check`, {}).then(r => r.data),
    onSuccess: (r) => {
      const verdict = r?.reddit || 'unknown';
      const emoji = verdict === 'clean' ? '✅' : verdict === 'captcha' ? '⚠️' : '❌';
      toast.success(`Proxy ${emoji} ${verdict}`, { description: r.external_ip ? `IP: ${r.external_ip}` : undefined });
      qc.invalidateQueries({ queryKey: ['agent', agent.id] });
    },
    onError: (e) => toast.error(e?.response?.data?.detail || 'Check failed'),
  });

  const { data: proxiesList } = useQuery({
    queryKey: ['proxies-list'], queryFn: () => api.get('/proxies').then(r => r.data),
  });
  const { data: credsList } = useQuery({
    queryKey: ['credentials-list'], queryFn: () => api.get('/credentials').then(r => r.data),
  });

  return (
    <div className="space-y-4">
      {/* Network & identity */}
      <Card title="Network & identity">
        <div className="space-y-4" data-testid="network-identity-card">
          {/* Proxy */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label>Residential proxy</Label>
              {proxy && (
                <div className="flex items-center gap-2">
                  <ProxyHealthChip health={proxy.health} />
                  {agent.proxy_id && (
                    <Button size="sm" variant="ghost" onClick={() => proxyCheckMut.mutate()} disabled={proxyCheckMut.isPending} data-testid="test-proxy-button">
                      {proxyCheckMut.isPending ? <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" /> : <Gauge className="mr-1 h-3.5 w-3.5" />}
                      Test
                    </Button>
                  )}
                </div>
              )}
            </div>
            <Select
              value={agent.proxy_id || 'none'}
              onValueChange={(v) => bindMut.mutate({ proxy_id: v === 'none' ? null : v })}
            >
              <SelectTrigger data-testid="proxy-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">— none (agent will be blocked on Reddit) —</SelectItem>
                {(proxiesList?.items || []).map(p => (
                  <SelectItem key={p.id} value={p.id}>
                    {p.label} · {p.country?.toUpperCase()} · {p.endpoint}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {proxy && (
              <div className="rounded-md border border-white/5 bg-[hsl(var(--surface-2))] p-3 text-xs space-y-1 font-mono">
                <div><span className="text-[hsl(var(--text-3))]">Endpoint:</span> {proxy.endpoint}</div>
                {proxy.health?.external_ip && <div><span className="text-[hsl(var(--text-3))]">Last IP observed:</span> <span className="text-[hsl(var(--text-1))]">{proxy.health.external_ip}</span></div>}
                {proxy.health?.checked_at && <div className="text-[hsl(var(--text-3))]">Checked {relativeTime(proxy.health.checked_at)}</div>}
              </div>
            )}
          </div>

          {/* Credential */}
          <div className="space-y-2 pt-3 border-t border-white/5">
            <div className="flex items-center justify-between">
              <Label>Reddit account (credential)</Label>
              <Button size="sm" variant="ghost" onClick={() => loginCheckMut.mutate()} disabled={loginCheckMut.isPending || !agent.credential_id} data-testid="test-login-button">
                {loginCheckMut.isPending ? <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" /> : <ShieldCheck className="mr-1 h-3.5 w-3.5" />}
                Test login
              </Button>
            </div>
            <Select
              value={agent.credential_id || 'none'}
              onValueChange={(v) => bindMut.mutate({ credential_id: v === 'none' ? null : v })}
            >
              <SelectTrigger data-testid="credential-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">— none (cookie-only mode) —</SelectItem>
                {(credsList?.items || []).map(c => (
                  <SelectItem key={c.id} value={c.id}>
                    {c.label} · u/{c.username}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {(credential || lastLoggedIn?.username) && (
              <div className="rounded-md border border-white/5 bg-[hsl(var(--surface-2))] p-3 text-xs space-y-1">
                {credential && <div><span className="text-[hsl(var(--text-3))]">Bound account:</span> <span className="font-mono text-[hsl(var(--text-1))]">u/{credential.username}</span></div>}
                {lastLoggedIn?.username ? (
                  <div><span className="text-[hsl(var(--text-3))]">Currently logged in as:</span> <span className="font-mono text-[hsl(var(--success))]">u/{lastLoggedIn.username}</span>{lastLoggedIn.checked_at && <span className="text-[hsl(var(--text-3))]"> · verified {relativeTime(lastLoggedIn.checked_at)}</span>}</div>
                ) : credential ? (
                  <div className="text-[hsl(var(--text-3))]">Not yet verified — click "Test login" to check live session.</div>
                ) : null}
              </div>
            )}
          </div>

          {/* TLS fingerprint diagnostic */}
          <div className="space-y-2 pt-3 border-t border-white/5">
            <div className="flex items-center justify-between gap-2">
              <Label>TLS fingerprint (per-agent, sticky)</Label>
              <RerollFingerprintButton agentId={agent.id} />
            </div>
            <AgentFingerprintChip agent={agent} />
            <TLSRotationCard proxyId={agent.proxy_id} agentProfile={agent.tls_profile} />
            <p className="text-[11px] text-[hsl(var(--text-3))] leading-relaxed">
              Each agent gets one persistent TLS+OS identity at creation. <strong>curl-cffi</strong> uses it for all server-side HTTP (probes, warmup hops, API scrapes). Camoufox's browser TLS is natively Firefox — Chrome/Safari profiles only diversify the server-side layer (useful anti-correlation across a fleet). Reroll when a fingerprint feels over-exposed.
            </p>
          </div>
        </div>
      </Card>

      <Card title="Agent configuration">
        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label>Name</Label>
            <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
          </div>
          <div className="space-y-1.5">
            <Label>Mission</Label>
            <Textarea rows={8} value={form.mission} onChange={(e) => setForm({ ...form, mission: e.target.value })} />
          </div>
          <div className="grid gap-3 md:grid-cols-3">
            <div className="space-y-1.5"><Label>Timezone</Label><Input value={form.timezone} onChange={(e) => setForm({ ...form, timezone: e.target.value })} /></div>
            <div className="space-y-1.5"><Label>Active hours start</Label><Input type="number" min={0} max={23} value={form.active_hours_start} onChange={(e) => setForm({ ...form, active_hours_start: Number(e.target.value) })} /></div>
            <div className="space-y-1.5"><Label>Active hours end</Label><Input type="number" min={0} max={23} value={form.active_hours_end} onChange={(e) => setForm({ ...form, active_hours_end: Number(e.target.value) })} /></div>
          </div>
          <Button onClick={() => mut.mutate(form)} disabled={mut.isPending} data-testid="agent-save-settings-button">
            {mut.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Save changes
          </Button>
        </div>
      </Card>
    </div>
  );
}

function ProxyHealthChip({ health }) {
  const verdict = health?.reddit;
  if (!verdict) return <span className="rounded-full border border-white/10 bg-[hsl(var(--surface-2))] px-2 py-0.5 text-[10px] uppercase tracking-wider text-[hsl(var(--text-3))]" data-testid="proxy-health-chip">untested</span>;
  const styles = verdict === 'clean' ? 'border-[hsl(var(--success))]/30 bg-[hsl(var(--success))]/10 text-[hsl(var(--success))]'
              : verdict === 'captcha' ? 'border-[hsl(var(--warning))]/30 bg-[hsl(var(--warning))]/10 text-[hsl(var(--warning))]'
              : 'border-[hsl(var(--danger))]/30 bg-[hsl(var(--danger))]/10 text-[hsl(var(--danger))]';
  return <span className={`rounded-full border px-2 py-0.5 text-[10px] uppercase tracking-wider ${styles}`} data-testid="proxy-health-chip">{verdict}</span>;
}

function StealthPanel({ agent, events, agentId, onWarmupCampaign, warmupPending, onClearCookies, clearingCookies }) {
  const cfg = React.useMemo(() => agent.config || {}, [agent.config]);
  const cookiesImported = !!cfg.seed_cookies_imported_at;
  const hasSession = !!cfg.seed_cookies_has_reddit_session;
  const warmupUntil = cfg.warmup_until_at ? new Date(cfg.warmup_until_at) : null;
  const warmupActive = warmupUntil && warmupUntil > new Date();
  const hoursRemaining = warmupActive ? Math.round((warmupUntil - new Date()) / 3600000) : 0;

  // Derive latest risk snapshot from events
  const latestRisk = React.useMemo(() => {
    const evs = (events || []).filter(e => e.kind === 'risk.snapshot');
    if (!evs.length) return null;
    return evs[0].payload;
  }, [events]);

  const preflight = React.useMemo(() => buildSignupPreflightSummary(events || [], cfg), [events, cfg]);
  const fidelity = React.useMemo(() => buildMobileFidelitySummary(events || [], cfg), [events, cfg]);

  return (
    <div className="grid gap-4 md:grid-cols-2">
      {/* Cookie Import */}
      <div className="rounded-lg border border-white/5 bg-[hsl(var(--surface-1))]">
        <div className="flex items-center justify-between border-b border-white/5 px-5 py-3">
          <h3 className="text-sm font-medium">Seed cookies</h3>
          {cookiesImported && <StatusDot state={hasSession ? 'completed' : 'idle'} label={hasSession ? 'session active' : 'partial'} />}
        </div>
        <div className="p-5 space-y-3">
          <p className="text-sm text-[hsl(var(--text-3))]">
            Import cookies from your own logged-in Chrome/Firefox to skip Reddit's login form entirely. The worker will restore the session on next run.
          </p>
          {cookiesImported ? (
            <div className="rounded-md border border-white/5 bg-[hsl(var(--surface-2))] p-3 text-xs">
              <div className="flex justify-between"><span className="text-[hsl(var(--text-3))]">Count</span><span className="font-mono">{cfg.seed_cookies_count}</span></div>
              <div className="flex justify-between mt-1"><span className="text-[hsl(var(--text-3))]">reddit_session</span><span className="font-mono">{hasSession ? 'yes' : 'no'}</span></div>
              <div className="flex justify-between mt-1"><span className="text-[hsl(var(--text-3))]">Imported</span><span className="font-mono">{formatDate(cfg.seed_cookies_imported_at)}</span></div>
            </div>
          ) : (
            <div className="text-xs text-[hsl(var(--text-3))]">No seed cookies imported yet.</div>
          )}
          <div className="flex gap-2">
            <CookieImportDialog agentId={agentId} onImported={() => window.location.reload()} />
            {cookiesImported && (
              <Button variant="secondary" size="sm" onClick={onClearCookies} disabled={clearingCookies} data-testid="clear-cookies-button">
                {clearingCookies && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Clear
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Warmup Campaign */}
      <div className="rounded-lg border border-white/5 bg-[hsl(var(--surface-1))]">
        <div className="flex items-center justify-between border-b border-white/5 px-5 py-3">
          <h3 className="text-sm font-medium">Variable warmup campaign</h3>
          {warmupActive && <StatusDot state="running" label={`${hoursRemaining}h left`} />}
        </div>
        <div className="p-5 space-y-3">
          <p className="text-sm text-[hsl(var(--text-3))]">
            Two-stage (generic web → Reddit) warmup, with Poisson-distributed session firings.
            Smart-login is gated while the window is active.
          </p>
          {warmupActive ? (
            <div className="rounded-md border border-[hsl(var(--warning))/0.35] bg-[hsl(var(--warning))/0.08] p-3 text-xs" data-testid="warmup-active-panel">
              <div className="font-medium">
                Active campaign · {cfg.warmup_campaign_preset || `${cfg.warmup_campaign_sessions} sessions`}
              </div>
              <div className="text-[hsl(var(--text-3))]">Ends {formatDate(cfg.warmup_until_at)}</div>
            </div>
          ) : cfg.warmup_campaign_started_at ? (
            <div className="rounded-md border border-white/5 bg-[hsl(var(--surface-2))] p-3 text-xs">
              <div>Last campaign: {cfg.warmup_campaign_preset || `${cfg.warmup_campaign_sessions} sessions`}</div>
              <div className="text-[hsl(var(--text-3))]">Ended {formatDate(cfg.warmup_until_at)}</div>
            </div>
          ) : null}
          <WarmupPresetGrid
            onPick={(preset) => onWarmupCampaign({ preset, include_generic_stage: true })}
            disabled={warmupPending || warmupActive}
            pending={warmupPending}
          />
        </div>
      </div>

      <MobileFidelityPanel fidelity={fidelity} />
      <SignupPreflightPanel preflight={preflight} agent={agent} agentId={agentId} />

      {/* Latest risk snapshot */}

      <div className="rounded-lg border border-white/5 bg-[hsl(var(--surface-1))] md:col-span-2">
        <div className="flex items-center justify-between border-b border-white/5 px-5 py-3">
          <h3 className="text-sm font-medium">Latest session risk signals</h3>
        </div>
        <div className="p-5">
          {latestRisk ? (
            <div className="grid gap-3 sm:grid-cols-4 text-sm">
              <RiskStat label="Risk score" value={latestRisk.risk_score?.toFixed(2)} hue={latestRisk.risk_score >= 0.6 ? 'danger' : latestRisk.risk_score >= 0.3 ? 'warning' : 'success'} />
              <RiskStat label="429s" value={latestRisk.status_429 ?? 0} hue={latestRisk.status_429 ? 'warning' : 'success'} />
              <RiskStat label="403s" value={latestRisk.status_403 ?? 0} hue={latestRisk.status_403 ? 'warning' : 'success'} />
              <RiskStat label="Captcha redirects" value={latestRisk.captcha_redirects ?? 0} hue={latestRisk.captcha_redirects ? 'danger' : 'success'} />
            </div>
          ) : (
            <div className="text-sm text-[hsl(var(--text-3))]">Run any task to populate risk signals.</div>
          )}
        </div>
      </div>
    </div>
  );
}

function buildSignupPreflightSummary(events, cfg) {
  const byKind = (kind) => events.find(e => e.kind === kind)?.payload || null;
  const kindStarts = (prefix) => events.find(e => e.kind?.startsWith(prefix)) || null;
  const result = byKind('signup.preflight.result') || cfg.signup_preflight_last || null;
  const blocked = byKind('signup.preflight.blocked') || null;
  const scoreInitial = byKind('signup.preflight.score.initial') || result?.recaptcha_initial || null;
  const scoreAfter = byKind('signup.preflight.score.after_warmup') || result?.recaptcha_after || null;
  const warmup = byKind('signup.preflight.warmup') || result?.warmup || null;
  const risk = byKind('signup.preflight.risk') || result?.risk || null;
  const captcha = kindStarts('signup.captchaai.')?.payload || null;
  const takeover = byKind('signup.requires_human_takeover') || null;
  const pauseReason = cfg.signup_preflight_pause_reason || blocked?.reason || result?.reason || null;
  return { result, blocked, scoreInitial, scoreAfter, warmup, risk, captcha, takeover, pauseReason };
}

function buildMobileFidelitySummary(events, cfg = {}) {
  const latestAudit = events.find(e => e.kind === 'mobile_fidelity.audit')?.payload || cfg.mobile_fidelity_last || null;
  const latestProbe = events.find(e => e.kind === 'mobile_fidelity.probe')?.payload || null;
  const latestRecaptcha = events.find(e => e.kind === 'mobile_fidelity.recaptcha')?.payload || null;
  const latestReddit = events.find(e => e.kind === 'mobile_fidelity.reddit_probe')?.payload || null;
  return { audit: latestAudit, probe: latestProbe, recaptcha: latestRecaptcha, reddit: latestReddit };
}

function MobileFidelityPanel({ fidelity }) {
  const hasData = !!(fidelity.audit || fidelity.probe || fidelity.recaptcha || fidelity.reddit);
  const score = fidelity.audit?.score ?? fidelity.probe?.score?.score;
  const verdict = fidelity.audit?.verdict || fidelity.probe?.score?.verdict || 'idle';
  const failures = fidelity.audit?.failures || fidelity.probe?.score?.failures || [];
  const hue = verdict === 'pass' ? 'success' : verdict === 'warn' ? 'warning' : hasData ? 'danger' : 'warning';
  const captchaScore = fidelity.audit?.recaptcha?.score ?? fidelity.recaptcha?.score;
  const redditRisk = fidelity.audit?.reddit_probe?.risk?.risk_score ?? fidelity.reddit?.risk?.risk_score;
  return (
    <div className="rounded-lg border border-white/5 bg-[hsl(var(--surface-1))] md:col-span-2" data-testid="mobile-fidelity-panel">
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-white/5 px-5 py-3">
        <div>
          <h3 className="text-sm font-medium">Mobile fidelity audit</h3>
          <div className="text-xs text-[hsl(var(--text-3))]">Pixel 7 spoof consistency before signup attempts</div>
        </div>
        <StatusDot state={verdict === 'pass' ? 'completed' : hasData ? 'failed' : 'idle'} label={verdict} />
      </div>
      <div className="p-5 space-y-4">
        {hasData ? (
          <>
            <div className="grid gap-3 sm:grid-cols-4 text-sm">
              <RiskStat label="Fidelity score" value={score == null ? '—' : Number(score).toFixed(0)} hue={hue} />
              <RiskStat label="Captcha score" value={captchaScore == null ? '—' : Number(captchaScore).toFixed(2)} hue={captchaScore == null ? 'warning' : Number(captchaScore) >= 0.7 ? 'success' : 'warning'} />
              <RiskStat label="Reddit risk" value={redditRisk == null ? '—' : Number(redditRisk).toFixed(2)} hue={redditRisk == null ? 'warning' : Number(redditRisk) > 0.25 ? 'danger' : 'success'} />
              <RiskStat label="Recommendation" value={fidelity.audit?.recommendation || '—'} hue={fidelity.audit?.recommendation === 'allow_signup' ? 'success' : 'warning'} />
            </div>
            {failures.length > 0 && (
              <div className="rounded-md border border-[hsl(var(--warning))/0.25] bg-[hsl(var(--warning))/0.08] p-3 text-xs" data-testid="mobile-fidelity-failures">
                <div className="font-medium text-[hsl(var(--warning))]">Mismatch signals</div>
                <ul className="mt-2 list-disc pl-5 text-[hsl(var(--text-2))] space-y-1">
                  {failures.slice(0, 8).map((f, i) => <li key={`${f}-${i}`}>{f}</li>)}
                </ul>
              </div>
            )}
            <div className="grid gap-3 md:grid-cols-2">
              <PreflightJsonBlock title="Probe score" value={fidelity.audit?.probe_score || fidelity.probe?.score} testId="mobile-fidelity-probe-json" />
              <PreflightJsonBlock title="Reddit probe" value={fidelity.audit?.reddit_probe || fidelity.reddit} testId="mobile-fidelity-reddit-json" />
            </div>
          </>
        ) : (
          <div className="text-sm text-[hsl(var(--text-3))]" data-testid="mobile-fidelity-empty-state">No mobile fidelity audit has run yet.</div>
        )}
      </div>
    </div>
  );
}

function SignupPreflightPanel({ preflight, agent, agentId }) {
  const hasData = !!(preflight.result || preflight.scoreInitial || preflight.warmup || preflight.risk || preflight.pauseReason || preflight.captcha || preflight.takeover);
  const score = preflight.scoreAfter?.score ?? preflight.scoreInitial?.score;
  const riskScore = preflight.risk?.risk_score;
  const status = preflight.blocked || preflight.pauseReason ? 'blocked' : preflight.result?.ok ? 'passed' : hasData ? 'observed' : 'idle';
  const borderClass = status === 'blocked'
    ? 'border-[hsl(var(--danger))/0.35]'
    : status === 'passed'
      ? 'border-[hsl(var(--success))/0.25]'
      : 'border-white/5';
  return (
    <div className={`rounded-lg border ${borderClass} bg-[hsl(var(--surface-1))] md:col-span-2`} data-testid="signup-preflight-panel">
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-white/5 px-5 py-3">
        <div>
          <h3 className="text-sm font-medium">Signup preflight</h3>
          <div className="text-xs text-[hsl(var(--text-3))]">Browser reputation gate before Reddit account creation</div>
        </div>
        <StatusDot state={status === 'blocked' ? 'failed' : status === 'passed' ? 'completed' : 'idle'} label={status} />
      </div>
      <div className="p-5 space-y-4">
        {hasData ? (
          <>
            <div className="grid gap-3 sm:grid-cols-4 text-sm">
              <RiskStat label="Captcha score" value={score == null ? '—' : Number(score).toFixed(2)} hue={score == null ? 'warning' : score < 0.7 ? 'danger' : 'success'} />
              <RiskStat label="Reddit risk" value={riskScore == null ? '—' : Number(riskScore).toFixed(2)} hue={riskScore == null ? 'warning' : riskScore > 0.25 ? 'danger' : 'success'} />
              <RiskStat label="Warmup loaded" value={preflight.warmup?.loaded_count ?? preflight.warmup?.sites_count ?? '—'} hue={(preflight.warmup?.loaded_count ?? 0) > 0 ? 'success' : 'warning'} />
              <RiskStat label="CaptchaAI" value={preflight.captcha?.verdict || (preflight.captcha?.ok ? 'ok' : '—')} hue={preflight.captcha?.ok ? 'success' : preflight.captcha ? 'warning' : 'success'} />
            </div>
            {preflight.pauseReason && (
              <div className="rounded-md border border-[hsl(var(--danger))/0.25] bg-[hsl(var(--danger))/0.08] p-3 text-xs" data-testid="signup-preflight-pause-reason">
                <div className="font-medium text-[hsl(var(--danger))]">Pause reason</div>
                <div className="mt-1 text-[hsl(var(--text-2))]">{preflight.pauseReason}</div>
              </div>
            )}
            {preflight.takeover && (
              <div className="rounded-md border border-[hsl(var(--warning))/0.25] bg-[hsl(var(--warning))/0.08] p-3 text-xs" data-testid="signup-preflight-takeover-alert">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div>
                    <div className="font-medium text-[hsl(var(--warning))]">Human takeover available</div>
                    <div className="mt-1 text-[hsl(var(--text-2))]">{preflight.takeover.hint || preflight.takeover.reason || 'Open takeover to finish the challenge manually.'}</div>
                  </div>
                  <TakeoverButton agentId={agentId} runtime={agent?.runtime} vmosPadCode={agent?.vmos_pad_code} />
                </div>
              </div>
            )}
            <div className="grid gap-3 md:grid-cols-2">
              <PreflightJsonBlock title="Warmup result" value={preflight.warmup} testId="signup-preflight-warmup-json" />
              <PreflightJsonBlock title="Risk probe" value={preflight.risk} testId="signup-preflight-risk-json" />
            </div>
          </>
        ) : (
          <div className="text-sm text-[hsl(var(--text-3))]" data-testid="signup-preflight-empty-state">No signup preflight events yet.</div>
        )}
      </div>
    </div>
  );
}

function PreflightJsonBlock({ title, value, testId }) {
  return (
    <div className="rounded-md border border-white/5 bg-[hsl(var(--surface-2))] p-3" data-testid={testId}>
      <div className="text-[11px] uppercase text-[hsl(var(--text-3))]">{title}</div>
      {value ? (
        <pre className="mt-2 max-h-44 overflow-auto text-xs font-mono text-[hsl(var(--text-2))]">{JSON.stringify(value, null, 2)}</pre>
      ) : (
        <div className="mt-2 text-xs text-[hsl(var(--text-3))]">No data</div>
      )}
    </div>
  );
}

function RiskStat({ label, value, hue }) {
  const hueClass = hue === 'danger' ? 'text-[hsl(var(--danger))]' : hue === 'warning' ? 'text-[hsl(var(--warning))]' : 'text-[hsl(var(--success))]';
  return (
    <div>
      <div className="text-[11px] uppercase tracking-wider text-[hsl(var(--text-3))]">{label}</div>
      <div className={`mt-1 text-xl font-semibold tabular ${hueClass}`}>{value}</div>
    </div>
  );
}

function CookieImportDialog({ agentId, onImported }) {
  const [open, setOpen] = React.useState(false);
  const [text, setText] = React.useState('');
  const [loading, setLoading] = React.useState(false);

  const submit = async () => {
    setLoading(true);
    try {
      let parsed;
      try { parsed = JSON.parse(text); } catch { toast.error('Not valid JSON'); setLoading(false); return; }
      const cookies = Array.isArray(parsed) ? parsed : (parsed.cookies || []);
      if (!cookies.length) { toast.error('No cookies in payload'); setLoading(false); return; }
      const r = await api.post(`/agents/${agentId}/cookies/import`, { cookies });
      toast.success(`Imported ${r.data.count} cookies`, { description: r.data.hint });
      setOpen(false);
      onImported?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Import failed');
    } finally { setLoading(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm" data-testid="import-cookies-button"><FileText className="mr-2 h-4 w-4" /> Import cookies</Button>
      </DialogTrigger>
      <DialogContent className="max-w-2xl" data-testid="import-cookies-dialog">
        <DialogHeader>
          <DialogTitle>Import Reddit cookies</DialogTitle>
          <DialogDescription>
            Export cookies from your logged-in browser (Chrome extension <span className="font-mono text-xs">Cookie-Editor</span> → Export → JSON), paste below. Must include <span className="font-mono text-xs">reddit_session</span> on reddit.com.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-2">
          <Label>Cookies JSON</Label>
          <Textarea rows={12} value={text} onChange={(e) => setText(e.target.value)} placeholder='[{"domain":".reddit.com","name":"reddit_session","value":"...","path":"/"...}]' className="font-mono text-xs" data-testid="cookies-json-input" />
        </div>
        <DialogFooter>
          <Button variant="secondary" onClick={() => setOpen(false)}>Cancel</Button>
          <Button onClick={submit} disabled={loading || !text.trim()} data-testid="cookies-import-submit">
            {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Import
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function PromotePanel({ agentId, agent, onPromote, onKarmaFarm, onBoost, onReplyMention, promotePending, karmaFarmPending, boostPending, replyMentionPending }) {
  const qc = useQueryClient();
  const { data: cfg, isLoading } = useQuery({
    queryKey: ['promote-config', agentId],
    queryFn: () => api.get(`/agents/${agentId}/promote-config`).then(r => r.data),
  });
  const [form, setForm] = React.useState({
    website: '', keywords: '', pain_points: '', tone: 'helpful, casual, no marketing speak',
    farm_niches: '', user_notes: '',
  });
  React.useEffect(() => {
    if (cfg) setForm({
      website: cfg.promote_website || '',
      keywords: (cfg.promote_keywords || []).join(', '),
      pain_points: cfg.promote_pain_points || '',
      tone: cfg.promote_tone || 'helpful, casual, no marketing speak',
      farm_niches: (cfg.farm_niches || []).join(', '),
      user_notes: cfg.user_notes || '',
    });
  }, [cfg]);

  const [promoteModel, setPromoteModel] = React.useState('anthropic/claude-sonnet-4.5');
  const [farmModel, setFarmModel] = React.useState('anthropic/claude-sonnet-4.5');
  const [boostModel, setBoostModel] = React.useState('google/gemini-2.5-flash');
  const [replyModel, setReplyModel] = React.useState('google/gemini-2.5-flash');

  const saveMut = useMutation({
    mutationFn: (body) => api.post(`/agents/${agentId}/promote-config`, body).then(r => r.data),
    onSuccess: () => { toast.success('Promote config saved'); qc.invalidateQueries({ queryKey: ['promote-config', agentId] }); qc.invalidateQueries({ queryKey: ['agent', agentId] }); },
    onError: (e) => toast.error(e?.response?.data?.detail || 'Save failed'),
  });
  const noteMut = useMutation({
    mutationFn: (note) => api.post(`/agents/${agentId}/context`, { note }).then(r => r.data),
    onSuccess: () => { toast.success('Context appended'); qc.invalidateQueries({ queryKey: ['promote-config', agentId] }); },
  });
  const [newNote, setNewNote] = React.useState('');

  const handleSave = () => {
    saveMut.mutate({
      website: form.website,
      keywords: form.keywords.split(',').map(s => s.trim()).filter(Boolean),
      pain_points: form.pain_points,
      tone: form.tone,
      farm_niches: form.farm_niches.split(',').map(s => s.trim()).filter(Boolean),
      user_notes: form.user_notes,
    });
  };

  const configured = !!form.website;

  return (
    <div className="space-y-4" data-testid="promote-panel">
      <div className="rounded-lg border border-white/5 bg-[hsl(var(--surface-1))]">
        <div className="border-b border-white/5 px-5 py-3 text-sm font-medium flex items-center gap-2">
          <Megaphone className="h-4 w-4 text-[hsl(var(--primary))]" /> Promote configuration
        </div>
        <div className="p-5 space-y-4">
          <div className="flex items-start justify-between gap-4">
            <p className="text-sm text-[hsl(var(--text-3))] flex-1">
              Set your target site and keywords. The agent LLM will discover relevant subreddits,
              craft contextual comments, and coordinate with sibling agents to boost visibility.
            </p>
            <BlueprintLibraryDialog agentId={agentId} onCloned={(c) => {
              setForm({
                website: c.promote_website || '',
                keywords: (c.promote_keywords || []).join(', '),
                pain_points: c.promote_pain_points || '',
                tone: c.promote_tone || '',
                farm_niches: (c.farm_niches || []).join(', '),
                user_notes: c.user_notes || '',
              });
              qc.invalidateQueries({ queryKey: ['promote-config', agentId] });
              qc.invalidateQueries({ queryKey: ['agent', agentId] });
            }} />
          </div>
          {cfg?.promote_blueprint_slug && (
            <div className="rounded-md border border-white/5 bg-[hsl(var(--surface-2))] px-3 py-2 text-xs text-[hsl(var(--text-3))] flex items-center gap-2" data-testid="applied-blueprint-badge">
              <Library className="h-3.5 w-3.5 text-[hsl(var(--primary))]" />
              Applied blueprint: <span className="font-mono text-[hsl(var(--text-1))]">{cfg.promote_blueprint_slug}</span>
            </div>
          )}
          {isLoading ? <div className="text-xs text-[hsl(var(--text-3))]">Loading…</div> : (
            <>
              <div className="space-y-1.5"><Label>Website</Label>
                <Input value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })} placeholder="https://example.com" data-testid="promote-website-input" />
              </div>
              <div className="space-y-1.5"><Label>Keywords (comma separated)</Label>
                <Input value={form.keywords} onChange={(e) => setForm({ ...form, keywords: e.target.value })} placeholder="productivity, note-taking, second brain" data-testid="promote-keywords-input" />
              </div>
              <div className="space-y-1.5"><Label>Pain points the tool solves</Label>
                <Textarea rows={3} value={form.pain_points} onChange={(e) => setForm({ ...form, pain_points: e.target.value })} placeholder="Losing track of ideas across apps, messy linking between notes, …" data-testid="promote-pain-points-input" />
              </div>
              <div className="grid gap-3 md:grid-cols-2">
                <div className="space-y-1.5"><Label>Tone</Label>
                  <Input value={form.tone} onChange={(e) => setForm({ ...form, tone: e.target.value })} data-testid="promote-tone-input" />
                </div>
                <div className="space-y-1.5"><Label>Karma-farm niches</Label>
                  <Input value={form.farm_niches} onChange={(e) => setForm({ ...form, farm_niches: e.target.value })} placeholder="productivity, AskReddit, todayilearned" data-testid="promote-niches-input" />
                </div>
              </div>
              <div className="space-y-1.5"><Label>Extra context / instructions for the LLM</Label>
                <Textarea rows={4} value={form.user_notes} onChange={(e) => setForm({ ...form, user_notes: e.target.value })} placeholder="Any extra framing. E.g. 'emphasize the free tier', 'avoid r/productivity (too strict)'…" data-testid="promote-notes-input" />
              </div>
              <Button onClick={handleSave} disabled={saveMut.isPending} data-testid="promote-save-button">
                {saveMut.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Save configuration
              </Button>
            </>
          )}
        </div>
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        <ActionCard
          testId="promote-action-card"
          icon={<Rocket className="h-4 w-4" />} title="Promote" disabled={!configured}
          desc="LLM picks a strategy (recent / high-engagement / Google-ranking), discovers relevant subs, posts one contextual comment mentioning your site."
          model={promoteModel} setModel={setPromoteModel}
          onGo={() => onPromote(promoteModel)} pending={promotePending}
          goLabel="Start Promote" goTestId="start-promote-button"
        />
        <ActionCard
          testId="karma-farm-action-card"
          icon={<Brain className="h-4 w-4" />} title="Karma farm" disabled={false}
          desc="Organic engagement in your niches — upvotes + 1 thoughtful reply per sub. Builds account reputation."
          model={farmModel} setModel={setFarmModel}
          onGo={() => onKarmaFarm(farmModel)} pending={karmaFarmPending}
          goLabel="Start Karma farm" goTestId="start-karma-farm-button"
        />
        <ActionCard
          testId="boost-action-card"
          icon={<ThumbsUp className="h-4 w-4" />} title="Boost comment" disabled={false}
          desc="Picks the next pending boost from the org queue (posted by any sibling agent) and upvotes + supports it."
          model={boostModel} setModel={setBoostModel}
          onGo={() => onBoost(boostModel)} pending={boostPending}
          goLabel="Boost next" goTestId="start-boost-button"
        />
        <ActionCard
          testId="reply-mention-action-card"
          icon={<MessageCircle className="h-4 w-4" />} title="Reply to mentions" disabled={!configured}
          desc="Searches Reddit for recent mentions of your site and posts a short genuine endorsement from this profile."
          model={replyModel} setModel={setReplyModel}
          onGo={() => onReplyMention(replyModel)} pending={replyMentionPending}
          goLabel="Find mentions" goTestId="start-reply-mention-button"
        />
      </div>

      <div className="rounded-lg border border-white/5 bg-[hsl(var(--surface-1))]">
        <div className="border-b border-white/5 px-5 py-3 text-sm font-medium flex items-center gap-2">
          <BookOpen className="h-4 w-4" /> Add context (feedback loop)
        </div>
        <div className="p-5 space-y-2">
          <p className="text-xs text-[hsl(var(--text-3))]">
            Append a note the agent will read on next run (e.g. "the last promote was too pushy, keep it shorter").
          </p>
          <Textarea rows={3} value={newNote} onChange={(e) => setNewNote(e.target.value)} placeholder="e.g. Don't post in r/productivity — rule breaker. Stick to r/selfimprovement." data-testid="context-note-input" />
          <Button size="sm" disabled={!newNote.trim() || noteMut.isPending} onClick={() => { noteMut.mutate(newNote); setNewNote(''); }} data-testid="context-note-submit">
            {noteMut.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Append to agent context
          </Button>
        </div>
      </div>
    </div>
  );
}

function ActionCard({ icon, title, desc, model, setModel, onGo, pending, goLabel, goTestId, testId, disabled }) {
  return (
    <div className="rounded-lg border border-white/5 bg-[hsl(var(--surface-1))]" data-testid={testId}>
      <div className="border-b border-white/5 px-5 py-3 text-sm font-medium flex items-center gap-2">{icon}{title}</div>
      <div className="p-5 space-y-3">
        <p className="text-xs text-[hsl(var(--text-3))]">{desc}</p>
        <div className="space-y-1.5">
          <Label className="text-xs">LLM model</Label>
          <Select value={model} onValueChange={setModel}>
            <SelectTrigger data-testid={`${goTestId}-model-select`}><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="anthropic/claude-sonnet-4.5">Claude Sonnet 4.5</SelectItem>
              <SelectItem value="google/gemini-2.5-flash">Gemini 2.5 Flash</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <Button size="sm" onClick={onGo} disabled={pending || disabled} data-testid={goTestId}>
          {pending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} {goLabel}
        </Button>
        {disabled && <div className="text-[11px] text-[hsl(var(--warning))]">Configure promote website above first.</div>}
      </div>
    </div>
  );
}

function InsightsPanel({ agentId }) {
  const { data, isLoading } = useQuery({
    queryKey: ['insights', agentId],
    queryFn: () => api.get(`/agents/${agentId}/insights`).then(r => r.data),
    refetchInterval: 15000,
  });
  if (isLoading) return <div className="text-sm text-[hsl(var(--text-3))]">Loading insights…</div>;
  const tasks = data?.recent_intelligent_tasks || [];
  const events = data?.events || [];
  return (
    <div className="space-y-4" data-testid="insights-panel">
      <div className="rounded-lg border border-white/5 bg-[hsl(var(--surface-1))]">
        <div className="border-b border-white/5 px-5 py-3 text-sm font-medium">Recent intelligent tasks</div>
        <div className="p-5">
          {tasks.length ? (
            <div className="divide-y divide-white/5">
              {tasks.map(t => (
                <div key={t.id} className="py-3" data-testid={`insight-task-${t.id}`}>
                  <div className="flex items-center gap-3 text-sm">
                    <span className="rounded border border-white/10 bg-[hsl(var(--surface-2))] px-1.5 py-0.5 font-mono text-[10px] uppercase tracking-wider">{t.kind}</span>
                    <StatusDot state={t.status} />
                    <span className="text-[hsl(var(--text-3))] font-mono text-xs">{relativeTime(t.created_at)}</span>
                    {t.result?.brain?.model && <span className="ml-auto text-xs text-[hsl(var(--text-3))]">{t.result.brain.model}</span>}
                  </div>
                  {t.result?.brain?.reflection && (
                    <div className="mt-1.5 text-xs text-[hsl(var(--text-2))] italic">"{t.result.brain.reflection}"</div>
                  )}
                  {t.result?.brain?.comment_url && (
                    <a href={t.result.brain.comment_url} target="_blank" rel="noreferrer" className="mt-1 text-xs font-mono text-[hsl(var(--primary))] underline block truncate">{t.result.brain.comment_url}</a>
                  )}
                </div>
              ))}
            </div>
          ) : <div className="text-sm text-[hsl(var(--text-3))]">No intelligent tasks yet. Configure Promote and run one.</div>}
        </div>
      </div>
      <div className="rounded-lg border border-white/5 bg-[hsl(var(--surface-1))]">
        <div className="border-b border-white/5 px-5 py-3 text-sm font-medium">Brain events</div>
        <div className="p-5">
          {events.length ? (
            <ol className="space-y-2">
              {events.map((e, i) => (
                <li key={i} className="text-xs font-mono" data-testid={`brain-event-${i}`}>
                  <span className="text-[hsl(var(--text-3))]">{relativeTime(e.created_at)}</span>{' '}
                  <span className="rounded border border-white/10 bg-[hsl(var(--surface-2))] px-1 py-0.5">{e.kind}</span>{' '}
                  <span className="text-[hsl(var(--text-2))]">{JSON.stringify(e.payload)}</span>
                </li>
              ))}
            </ol>
          ) : <div className="text-sm text-[hsl(var(--text-3))]">No brain events yet.</div>}
        </div>
      </div>
    </div>
  );
}


const BLUEPRINT_ICONS = {
  zap: Zap, terminal: Terminal, sparkles: Sparkles, 'trending-up': TrendingUp,
  heart: Heart, 'shopping-bag': ShoppingBag, 'book-open': BookOpen,
  smartphone: Smartphone, briefcase: Briefcase, 'git-branch': GitBranch,
};

function BlueprintLibraryDialog({ agentId, onCloned }) {
  const [open, setOpen] = React.useState(false);
  const [selected, setSelected] = React.useState(null);
  const [website, setWebsite] = React.useState('');
  const [extraNotes, setExtraNotes] = React.useState('');

  const { data, isLoading } = useQuery({
    queryKey: ['blueprints'],
    queryFn: () => api.get('/blueprints').then(r => r.data),
    enabled: open,
  });

  const cloneMut = useMutation({
    mutationFn: (body) => api.post('/blueprints/clone', body).then(r => r.data),
    onSuccess: (r) => {
      toast.success(`Applied "${r.blueprint}" blueprint`, { description: 'Promote tab has been prefilled.' });
      onCloned?.(r.config);
      setOpen(false);
      setSelected(null);
      setWebsite('');
      setExtraNotes('');
    },
    onError: (e) => toast.error(e?.response?.data?.detail || 'Clone failed'),
  });

  const categories = React.useMemo(() => {
    const cats = new Set((data?.items || []).map(b => b.category));
    return ['all', ...Array.from(cats)];
  }, [data]);
  const [activeCat, setActiveCat] = React.useState('all');
  const filtered = React.useMemo(() => {
    const items = data?.items || [];
    return activeCat === 'all' ? items : items.filter(b => b.category === activeCat);
  }, [data, activeCat]);

  const apply = () => {
    if (!selected || !website.trim()) return;
    cloneMut.mutate({ agent_id: agentId, slug: selected.slug, website: website.trim(), extra_notes: extraNotes.trim() || null });
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) setSelected(null); }}>
      <DialogTrigger asChild>
        <Button variant="secondary" size="sm" data-testid="open-blueprint-library-button">
          <Library className="mr-2 h-4 w-4" /> Browse blueprints
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-hidden flex flex-col" data-testid="blueprint-library-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Library className="h-4 w-4 text-[hsl(var(--primary))]" /> Promote blueprint library</DialogTitle>
          <DialogDescription>
            Clone a proven config for your product category. You only need to enter your website.
          </DialogDescription>
        </DialogHeader>

        {!selected ? (
          <div className="flex-1 overflow-y-auto -mx-6 px-6">
            <div className="flex flex-wrap gap-1.5 mb-3">
              {categories.map(cat => (
                <button
                  key={cat}
                  onClick={() => setActiveCat(cat)}
                  data-testid={`blueprint-filter-${cat}`}
                  className={`text-[11px] uppercase tracking-wider px-2 py-1 rounded border transition-colors ${activeCat === cat ? 'border-[hsl(var(--primary))] bg-[hsl(var(--primary))]/10 text-[hsl(var(--text-1))]' : 'border-white/5 bg-[hsl(var(--surface-2))] text-[hsl(var(--text-3))] hover:text-[hsl(var(--text-1))]'}`}
                >{cat}</button>
              ))}
            </div>
            {isLoading ? <div className="text-sm text-[hsl(var(--text-3))]">Loading blueprints…</div> : (
              <div className="grid gap-2 sm:grid-cols-2">
                {filtered.map(b => {
                  const Icon = BLUEPRINT_ICONS[b.icon] || Rocket;
                  return (
                    <button
                      key={b.slug}
                      onClick={() => setSelected(b)}
                      data-testid={`blueprint-card-${b.slug}`}
                      className="group text-left rounded-lg border border-white/5 bg-[hsl(var(--surface-1))] hover:border-[hsl(var(--primary))]/40 hover:bg-[hsl(var(--surface-2))] transition-colors p-4 space-y-2"
                    >
                      <div className="flex items-center gap-2">
                        <span className="rounded-md border border-white/10 bg-[hsl(var(--surface-2))] p-1.5 group-hover:border-[hsl(var(--primary))]/40">
                          <Icon className="h-4 w-4 text-[hsl(var(--primary))]" />
                        </span>
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-medium">{b.name}</div>
                          <div className="text-[11px] uppercase tracking-wider text-[hsl(var(--text-3))]">{b.category}</div>
                        </div>
                      </div>
                      <p className="text-xs text-[hsl(var(--text-2))]">{b.tagline}</p>
                      <div className="flex flex-wrap gap-1">
                        {(b.config.keywords || []).slice(0, 4).map(k => (
                          <span key={k} className="text-[10px] font-mono rounded border border-white/5 bg-black/20 px-1.5 py-0.5 text-[hsl(var(--text-3))]">{k}</span>
                        ))}
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        ) : (
          <div className="flex-1 overflow-y-auto -mx-6 px-6 space-y-4" data-testid="blueprint-preview">
            <button onClick={() => setSelected(null)} className="text-xs text-[hsl(var(--text-3))] hover:text-[hsl(var(--text-1))] inline-flex items-center gap-1" data-testid="blueprint-back-button">
              <ChevronLeft className="h-3.5 w-3.5" /> Back to library
            </button>
            <div className="rounded-lg border border-white/5 bg-[hsl(var(--surface-2))] p-4 space-y-3">
              <div className="flex items-start gap-3">
                {React.createElement(BLUEPRINT_ICONS[selected.icon] || Rocket, { className: 'h-5 w-5 text-[hsl(var(--primary))] mt-0.5' })}
                <div>
                  <div className="text-sm font-medium">{selected.name}</div>
                  <div className="text-xs text-[hsl(var(--text-3))]">{selected.tagline}</div>
                </div>
              </div>
              <p className="text-xs text-[hsl(var(--text-2))]">{selected.description}</p>
              <dl className="grid grid-cols-[110px_1fr] gap-y-1.5 text-xs">
                <dt className="text-[hsl(var(--text-3))]">Keywords</dt>
                <dd className="text-[hsl(var(--text-2))]">{(selected.config.keywords || []).join(', ')}</dd>
                <dt className="text-[hsl(var(--text-3))]">Niches</dt>
                <dd className="text-[hsl(var(--text-2))]">{(selected.config.farm_niches || []).map(n => `r/${n}`).join(', ')}</dd>
                <dt className="text-[hsl(var(--text-3))]">Tone</dt>
                <dd className="text-[hsl(var(--text-2))] italic">"{selected.config.tone}"</dd>
                <dt className="text-[hsl(var(--text-3))]">Pain points</dt>
                <dd className="text-[hsl(var(--text-2))]">{selected.config.pain_points}</dd>
                <dt className="text-[hsl(var(--text-3))]">LLM notes</dt>
                <dd className="text-[hsl(var(--text-2))] whitespace-pre-wrap">{selected.config.user_notes}</dd>
              </dl>
            </div>
            <div className="space-y-1.5">
              <Label>Your website</Label>
              <Input value={website} onChange={(e) => setWebsite(e.target.value)} placeholder="https://yourproduct.com" data-testid="blueprint-website-input" />
            </div>
            <div className="space-y-1.5">
              <Label>Extra notes (optional, prepended to blueprint's LLM notes)</Label>
              <Textarea rows={2} value={extraNotes} onChange={(e) => setExtraNotes(e.target.value)} placeholder="Any extra framing you want the agent to remember..." data-testid="blueprint-extra-notes-input" />
            </div>
          </div>
        )}

        <DialogFooter>
          <Button variant="secondary" onClick={() => setOpen(false)}>Cancel</Button>
          {selected && (
            <Button onClick={apply} disabled={!website.trim() || cloneMut.isPending} data-testid="blueprint-apply-button">
              {cloneMut.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Apply blueprint
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


function TLSRotationCard({ proxyId, agentProfile }) {
  const [result, setResult] = React.useState(null);
  const [impersonate, setImpersonate] = React.useState(agentProfile || 'firefox135');
  React.useEffect(() => { if (agentProfile) setImpersonate(agentProfile); }, [agentProfile]);
  const mut = useMutation({
    mutationFn: () => api.post('/stealth/tls-check', { proxy_id: proxyId, impersonate }).then(r => r.data),
    onSuccess: (d) => { setResult(d); toast.success(d.match_firefox ? 'TLS matches real Firefox ✅' : 'No Firefox match'); },
    onError: (e) => toast.error(e?.response?.data?.detail || 'TLS check failed'),
  });
  const match = result?.match_firefox;
  const chipStyles = match === true ? 'border-[hsl(var(--success))]/30 bg-[hsl(var(--success))]/10 text-[hsl(var(--success))]'
                   : match === false ? 'border-[hsl(var(--warning))]/30 bg-[hsl(var(--warning))]/10 text-[hsl(var(--warning))]'
                   : 'border-white/10 bg-[hsl(var(--surface-2))] text-[hsl(var(--text-3))]';
  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2 flex-wrap">
        <span className={`rounded-full border px-2 py-0.5 text-[10px] uppercase tracking-wider ${chipStyles}`} data-testid="tls-chip">
          {match === true ? `✓ Firefox 135 match${proxyId ? ' via proxy' : ''}` : match === false ? '⚠ not matching' : 'untested'}
        </span>
        <Select value={impersonate} onValueChange={setImpersonate}>
          <SelectTrigger className="w-40 h-7 text-xs" data-testid="tls-profile-select"><SelectValue /></SelectTrigger>
          <SelectContent>
            {BROWSER_PROFILES.map((p) => (
              <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Button size="sm" variant="ghost" onClick={() => mut.mutate()} disabled={mut.isPending} data-testid="run-tls-check-button">
          {mut.isPending ? <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" /> : <Gauge className="mr-1 h-3.5 w-3.5" />}
          Run TLS check
        </Button>
      </div>
      {result && (
        <div className="rounded-md border border-white/5 bg-[hsl(var(--surface-2))] p-3 text-[11px] font-mono space-y-0.5" data-testid="tls-details">
          <div><span className="text-[hsl(var(--text-3))]">JA3 hash:</span> {result.ja3_hash || '—'}</div>
          <div><span className="text-[hsl(var(--text-3))]">HTTP:</span> {result.http_version || '—'}</div>
          <div><span className="text-[hsl(var(--text-3))]">User-Agent observed:</span> <span className="text-[hsl(var(--text-1))]">{result.user_agent || '—'}</span></div>
          {result.via_proxy && <div className="text-[hsl(var(--text-3))]">(observed via your residential proxy)</div>}
          {result.error && <div className="text-[hsl(var(--danger))]">error: {result.error}</div>}
        </div>
      )}
    </div>
  );
}


function AgentFingerprintChip({ agent }) {
  const tls = agent?.tls_profile || 'firefox135';
  const os = agent?.browser_os || 'windows';
  const osLabel = { windows: 'Windows', macos: 'macOS', linux: 'Linux', android: 'Android' }[os] || os;
  const consistentWithCamoufox = tls.startsWith('firefox');
  const farm = (agent?.runtime || 'browser') === 'mobile' ? 'Mobile farm' : 'Browser farm';
  return (
    <div className="rounded-md border border-white/5 bg-[hsl(var(--surface-2))] p-3 text-xs flex items-center gap-3 flex-wrap" data-testid="agent-fingerprint-chip">
      <div>
        <div className="text-[hsl(var(--text-3))] text-[10px] uppercase tracking-wider">Assigned identity</div>
        <div className="font-mono text-[hsl(var(--text-1))]">{browserProfileLabel(tls)} · {osLabel}</div>
        <div className="mt-0.5 text-[10px] text-[hsl(var(--text-3))]">{farm}</div>
      </div>
      {!consistentWithCamoufox && (agent?.runtime || 'browser') !== 'mobile' && (
        <div className="text-[11px] text-[hsl(var(--warning))] max-w-md">
          Browser TLS stays Firefox (Camoufox). Only server-side HTTP sends {browserProfileLabel(tls)}-shaped TLS.
        </div>
      )}
    </div>
  );
}

function RerollFingerprintButton({ agentId }) {
  const qc = useQueryClient();
  const mut = useMutation({
    mutationFn: () => api.post(`/agents/${agentId}/reroll-fingerprint`).then(r => r.data),
    onSuccess: (r) => {
      toast.success(`New fingerprint: ${r.tls_profile} on ${r.browser_os}`, { description: 'Next task will use this identity.' });
      qc.invalidateQueries({ queryKey: ['agent', agentId] });
    },
  });
  return (
    <Button size="sm" variant="ghost" onClick={() => mut.mutate()} disabled={mut.isPending} data-testid="reroll-fingerprint-button">
      {mut.isPending ? <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" /> : <Sparkles className="mr-1 h-3.5 w-3.5" />}
      Reroll
    </Button>
  );
}

